import type { TradeItem } from '@/types';
import { getExchangeRate, getExchangeRateSource } from '@/services/currency/currency-service';
import { getTradeTotalValue } from './trade-math';

/**
 * Currency-aware aggregation for the Trades / MTM totals (LR-469).
 *
 * The platform books trades in their own currency, so summing different
 * currencies into a single native number is meaningless (USD 100 + EUR 100 is
 * neither). This module is the single primitive for "honest per-currency
 * subtotals + one convenience roll-up", mirroring the per-currency split that
 * `computeMtmExposure` already does for MtM exposure (LR-387).
 *
 * The roll-up (`equiv`) is the ONLY cross-currency number; it converts each
 * native bucket to `base` via spot FX (`getExchangeRate`, reused — no FX math
 * is redefined here) and is flagged `usedFallback` when any conversion fell
 * back to a stale/default rate, so the UI can render it as approximate (≈/⚠).
 */

export type CurrencyBuckets = Record<string, number>;

export interface CurrencyAggregate {
  /** Native per-currency subtotals — never summed across currencies. */
  byCcy: CurrencyBuckets;
  /** Convenience roll-up of every bucket converted into `base` (spot FX). */
  equiv: number;
  /** Base currency of `equiv` (e.g. 'EUR'). */
  base: string;
  /** True when any non-trivial conversion used a fallback/default rate. */
  usedFallback: boolean;
  /** The non-live currencies behind `usedFallback`, sorted base-first then
   *  alphabetically — lets the UI name them ("RUB rate approximate") instead of
   *  a generic warning. Empty when every conversion was live. */
  approxCurrencies: string[];
  /** |base-equiv of the approx currencies| ÷ |gross base-equiv of all buckets|
   *  (0..1). Lets a caller gate the ⚠ on MATERIALITY: a sub-1% non-live sliver in
   *  a base-currency-dominated total isn't worth a warning. 0 when nothing is
   *  approximate (or the gross roll-up is 0). */
  approxShare: number;
  /** Currencies present, sorted base-first then alphabetically. */
  currencies: string[];
}

const sortBaseFirst = (base: string) => (a: string, b: string): number =>
  a === base ? -1 : b === base ? 1 : a.localeCompare(b);

/**
 * Roll a per-currency integer-cent map up into a `CurrencyAggregate`: each
 * non-base bucket is converted into `baseUpper` via spot FX, with approx
 * detection and base-first ordering. Shared by `aggregateByCurrency` (row pass)
 * and `aggregateFromBuckets` (pre-summed map) so the roll-up math lives once.
 */
const rollUpCents = (cents: Record<string, number>, baseUpper: string): CurrencyAggregate => {
  const byCcy: CurrencyBuckets = {};
  let equivCents = 0;
  // Gross (sum of |bucket equiv|) is the materiality denominator: a netting total
  // can be ~0 while individual buckets are large, so |net| would over-state any
  // single bucket's share. The approx numerator sums only the non-live buckets.
  let grossEquivCents = 0;
  let approxEquivCents = 0;
  const approxSet = new Set<string>();

  for (const [ccy, c] of Object.entries(cents)) {
    byCcy[ccy] = c / 100;
    let bucketEquivCents: number;
    if (ccy === baseUpper) {
      bucketEquivCents = c;
    } else {
      if (getExchangeRateSource(ccy, baseUpper) !== 'live') approxSet.add(ccy);
      bucketEquivCents = Math.round((c / 100) * getExchangeRate(ccy, baseUpper) * 100);
    }
    equivCents += bucketEquivCents;
    grossEquivCents += Math.abs(bucketEquivCents);
    if (approxSet.has(ccy)) approxEquivCents += Math.abs(bucketEquivCents);
  }

  return {
    byCcy,
    equiv: equivCents / 100,
    base: baseUpper,
    usedFallback: approxSet.size > 0,
    approxCurrencies: [...approxSet].sort(sortBaseFirst(baseUpper)),
    approxShare: grossEquivCents > 0 ? approxEquivCents / grossEquivCents : 0,
    currencies: Object.keys(byCcy).sort(sortBaseFirst(baseUpper)),
  };
};

/**
 * Group `rows` by currency (`ccyOf`, default `row.currency`), sum `valueFn`
 * per currency (native), then roll the buckets up into `base` via spot FX.
 * Uses integer-cent accumulation to avoid float drift across large books.
 *
 * @param valueFn per-row contribution — MUST be denominated in the currency
 *   `ccyOf` returns for that row (defaults to `getTradeTotalValue`). Pass a
 *   column-specific accessor for footer column totals. NOTE: re-keying via
 *   `ccyOf` without switching `valueFn` to amounts in that same currency
 *   silently labels e.g. USD figures with a EUR bucket — change both together
 *   (LR-474: settlement buckets pair `getEffectiveSettlementCurrency` with
 *   settlement amounts).
 * @param ccyOf bucket key per row; defaults to the security currency.
 */
export const aggregateByCurrency = (
  rows: TradeItem[],
  valueFn: (t: TradeItem) => number = getTradeTotalValue,
  base = 'EUR',
  ccyOf?: (t: TradeItem) => string,
): CurrencyAggregate => {
  const baseUpper = base.toUpperCase();
  const cents: Record<string, number> = {};

  for (const t of rows) {
    const value = valueFn(t);
    if (!Number.isFinite(value)) continue;
    const ccy = ((ccyOf ? ccyOf(t) : t.currency) || baseUpper).toUpperCase();
    cents[ccy] = (cents[ccy] ?? 0) + Math.round(value * 100);
  }

  return rollUpCents(cents, baseUpper);
};

/**
 * Like `aggregateByCurrency` but over an ALREADY per-currency-summed native map
 * (e.g. the MTM aggregate bar's per-bucket totals) — same base-FX roll-up,
 * approx detection and ordering, just without the row pass. Non-finite values
 * are dropped; keys are upper-cased so 'eur'/'EUR' merge into one bucket.
 */
export const aggregateFromBuckets = (
  byCcy: Record<string, number>,
  base = 'EUR',
): CurrencyAggregate => {
  const baseUpper = base.toUpperCase();
  const cents: Record<string, number> = {};
  for (const [ccy, value] of Object.entries(byCcy)) {
    if (!Number.isFinite(value)) continue;
    const key = (ccy || baseUpper).toUpperCase();
    cents[key] = (cents[key] ?? 0) + Math.round(value * 100);
  }
  return rollUpCents(cents, baseUpper);
};
